// File Metadata:
// Filename: authApi.js
// Version: 1.01
// Created: 2025-06-16
// Last Modified: 2026-02-09
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: RTK Query API slice for authentication operations - Added verifyTwoFactor mutation

import { baseApi } from '../../../store/api/baseApi';

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Login mutation
    login: builder.mutation({
      query: ({ email, password, captchaToken }) => ({
        url: '/auth/login',
        method: 'POST',
        body: { email, password, captchaToken },
      }),
      invalidatesTags: ['User', 'Profile', 'Users'],
      transformResponse: (response) => {
        // Store token and user data
        return response;
      },
    }),

    // Logout mutation
    logout: builder.mutation({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['User', 'Profile', 'Users', 'Checklist', 'Chore', 'Chat', 'Todo', 'Reward'],
    }),

    // Get current user query
    getMe: builder.query({
      query: () => '/auth/me',
      providesTags: ['User'],
      keepUnusedDataFor: 300, // Keep user data for 5 minutes
    }),

    // Refresh token mutation
    refreshToken: builder.mutation({
      query: () => ({
        url: '/auth/refresh',
        method: 'POST',
      }),
    }),

    // Register mutation (if you have registration)
    register: builder.mutation({
      query: (userData) => ({
        url: '/auth/register',
        method: 'POST',
        body: userData,
      }),
    }),

    // Forgot password mutation
    forgotPassword: builder.mutation({
      query: ({ email }) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body: { email },
      }),
    }),

    // Reset password mutation
    resetPassword: builder.mutation({
      query: ({ token, password }) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body: { token, password },
      }),
    }),

    // Change password mutation
    changePassword: builder.mutation({
      query: ({ currentPassword, newPassword }) => ({
        url: '/auth/change-password',
        method: 'POST',
        body: { currentPassword, newPassword },
      }),
      invalidatesTags: ['User'],
    }),

    // Verify 2FA for login
    verifyTwoFactor: builder.mutation({
      query: ({ token }) => ({
        url: '/auth/2fa/verify',
        method: 'POST',
        body: { token },
      }),
      invalidatesTags: ['User'],
    }),

    // Verify email mutation
    verifyEmail: builder.mutation({
      query: ({ token }) => ({
        url: '/auth/verify-email',
        method: 'POST',
        body: { token },
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

// Export hooks for use in components
export const {
  useLoginMutation,
  useLogoutMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useRefreshTokenMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useVerifyEmailMutation,
  useVerifyTwoFactorMutation,
} = authApi;
